import React, { useRef, useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import BillingModal from './BillingModal';
import './Header.css';
import ElectronControls from './ElectronControls';


const Header = ({ setShowAuthModal, onNavigate, interviewInProgress, openPricingRef }) => {
  const { user, logout, credits, fetchCredits, avatarUrl } = useUser();
  const headerRef = useRef(null);
  const mobileMenuToggleRef = useRef(null);
  const mobileNavRef = useRef(null);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [showDropdown, setShowDropdown] = useState(false);
  const [showPricingModal, setShowPricingModal] = useState(false);
  // Cadence selector for the Lite/Pro/Max pricing cards. One of: monthly | 6months | yearly
  const [pricingCadence, setPricingCadence] = useState('monthly');
  const [showBillingModal, setShowBillingModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [pricing, setPricing] = useState(null);
  const [loadingPricing, setLoadingPricing] = useState(false);
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

  // Function to determine credit display and status based on subscription
  const getCreditsInfo = () => {
    if (!user) {
      return { 
        display: '3', 
        status: 'guest',
        showUnlimited: false,
        showActionRequired: false
      };
    }
    
    const userCredits = credits || 0;
    const today = new Date();
    const subscriptionEnd = user.subscription_end ? new Date(user.subscription_end) : null;
    const hasActiveSubscription = user.subscription_status === 'active' || user.subscription_status === 'cancelling';

    // FALLBACK: If subscription fields are not in user object, use credits as indicator
    // Credits >= 999000 means active subscription (unlimited access)
    if (userCredits >= 999000) {
      return {
        display: 'Unlimited',
        status: 'active',
        showUnlimited: true,
        showActionRequired: false
      };
    }
    
    // Compare dates without time - set to start of day for accurate comparison
    let isWithinSubscriptionPeriod = false;
    if (subscriptionEnd) {
      const todayDateOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
      const endDateOnly = new Date(subscriptionEnd.getFullYear(), subscriptionEnd.getMonth(), subscriptionEnd.getDate());
      isWithinSubscriptionPeriod = todayDateOnly <= endDateOnly;
    }
    
    // PRIORITY 1: Check if user has active subscription first (if data available)
    if (hasActiveSubscription && isWithinSubscriptionPeriod) {
      return {
        display: 'Unlimited',
        status: 'active',
        showUnlimited: true,
        showActionRequired: false
      };
    }
    
    // PRIORITY 2: Subscription expired - show 0 credits
    if (subscriptionEnd && !isWithinSubscriptionPeriod) {
      return {
        display: '0',
        status: 'expired',
        showUnlimited: false,
        showActionRequired: true
      };
    }
    
    // PRIORITY 3: Trial users (1-10 credits, no subscription data)
    if (userCredits > 0 && userCredits <= 10) {
      return {
        display: userCredits.toString(),
        status: 'trial',
        showUnlimited: false,
        showActionRequired: false
      };
    }
    
    // PRIORITY 4: No credits - need to subscribe
    return {
      display: '0',
      status: 'no-credits',
      showUnlimited: false,
      showActionRequired: true
    };
  };

  const creditsInfo = getCreditsInfo();

  // Derive the human-readable tier label from `user.subscription_plan`.
  // The DB stores plans as `<tier>-<cadence>` (e.g. 'lite-monthly',
  // 'pro-yearly', 'max-6months'). We surface just the tier on the badge
  // so users on Lite don't see a misleading "Pro" chip.
  const planTierLabel = (() => {
    const raw = (user?.subscription_plan || '').toString().toLowerCase();
    if (raw.startsWith('lite')) return 'Lite';
    if (raw.startsWith('max'))  return 'Max';
    if (raw.startsWith('pro'))  return 'Pro';
    // Legacy plan names ('monthly', 'yearly', '6-months') didn't carry a
    // tier prefix — those rows historically meant the old single-tier
    // (now Pro). Fall back accordingly.
    if (['monthly', 'yearly', '6-months', '3-months'].includes(raw)) return 'Pro';
    return 'Pro';
  })();

  // Expose pricing opener to parent via ref
  useEffect(() => {
    if (openPricingRef) openPricingRef.current = () => setShowPricingModal(true);
  }, [openPricingRef]);

  // Fetch pricing when modal opens
  useEffect(() => {
    if (showPricingModal && !pricing) {
      fetchPricing();
    }
  }, [showPricingModal, pricing]);

  const fetchPricing = async () => {
    setLoadingPricing(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/pricing`);
      const data = await response.json();
      
      if (data.success) {
        setPricing(data.plans);
      } else {
        console.error('Failed to fetch pricing');
      }
    } catch (error) {
      console.error('Error fetching pricing:', error);
    } finally {
      setLoadingPricing(false);
    }
  };

  // Fetch credits when user logs in
  useEffect(() => {
    if (user && fetchCredits) {
      fetchCredits();
    }
  }, [user, fetchCredits]);

  const handleNavClick = (e, targetId) => {
    e.preventDefault();
    
    const navigateToSection = () => {
      if (location.pathname !== '/') {
        sessionStorage.setItem('scrollTarget', targetId);
        if (onNavigate) {
          onNavigate('/');
        } else {
          navigate('/');
        }
      } else {
        const target = document.querySelector(targetId);
        if (target) {
          target.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      }
    };

    if (interviewInProgress && onNavigate) {
      onNavigate('/').then(() => {
        setTimeout(() => {
          const target = document.querySelector(targetId);
          if (target) {
            target.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
          }
        }, 100);
      });
    } else {
      navigateToSection();
    }
  };

  const handlePricingClick = (e) => {
    e.preventDefault();
    setShowPricingModal(true);
  };

  const closeBillingModal = useCallback(() => setShowBillingModal(false), []);

  const handleScroll = useCallback(() => {
    const header = headerRef.current;
    if (!header) return;
    
    const scrolled = window.pageYOffset;
    
    if (scrolled > 50) {
      header.classList.add('scrolled');
    } else {
      header.classList.remove('scrolled');
    }
  }, []);

  const updateActiveMenuItem = useCallback(() => {
    const sections = document.querySelectorAll('section[id]');
    const navLinks = document.querySelectorAll('.nav-links a, .mobile-nav a');
    
    let currentSection = '';
    const scrollPos = window.pageYOffset + 100;
    
    sections.forEach(section => {
      const sectionTop = section.offsetTop;
      const sectionHeight = section.offsetHeight;
      
      if (scrollPos >= sectionTop && scrollPos < sectionTop + sectionHeight) {
        currentSection = section.getAttribute('id');
      }
    });
    
    navLinks.forEach(link => {
      link.classList.remove('active');
      if (link.getAttribute('href') === `#${currentSection}`) {
        link.classList.add('active');
      }
    });
  }, []);

  useEffect(() => {
    const mobileMenuToggle = mobileMenuToggleRef.current;
    const mobileNav = mobileNavRef.current;

    const toggleMenu = () => {
      mobileMenuToggle?.classList.toggle('active');
      mobileNav?.classList.toggle('active');
    };

    if (mobileMenuToggle) {
      mobileMenuToggle.addEventListener('click', toggleMenu);
    }

    const mobileNavLinks = document.querySelectorAll('.mobile-nav a');
    mobileNavLinks.forEach(link => {
      link.addEventListener('click', () => {
        if (mobileMenuToggle) mobileMenuToggle.classList.remove('active');
        if (mobileNav) mobileNav.classList.remove('active');
      });
    });

    const handleClickOutside = (e) => {
      if (mobileMenuToggle && !mobileMenuToggle.contains(e.target) && 
          mobileNav && !mobileNav.contains(e.target)) {
        mobileMenuToggle.classList.remove('active');
        mobileNav.classList.remove('active');
      }

      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };

    const handleOpenPricing = () => setShowPricingModal(true);
    document.addEventListener('openPricingModal', handleOpenPricing);

    document.addEventListener('click', handleClickOutside);
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('scroll', updateActiveMenuItem);
    window.addEventListener('load', updateActiveMenuItem);

    return () => {
      if (mobileMenuToggle) {
        mobileMenuToggle.removeEventListener('click', toggleMenu);
      }
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('openPricingModal', handleOpenPricing);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('scroll', updateActiveMenuItem);
      window.removeEventListener('load', updateActiveMenuItem);
    };
  }, [handleScroll, updateActiveMenuItem]);

  const handleLogout = async () => {
    if (interviewInProgress && onNavigate) {
      const confirmEnd = window.confirm(
        'Interview is currently in progress. Do you want to end the interview and save your session before logging out?'
      );
      if (!confirmEnd) {
        setShowDropdown(false);
        return;
      }
      await onNavigate('/logout-redirect');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/logout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ email: user?.email })
      });

      const data = await response.json();
      
      if (response.ok) {
        console.log('Logout successful:', data);
      } else {
        console.log('Logout API error, but proceeding with frontend logout:', data.message);
      }
    } catch (error) {
      console.error('Logout API error, but proceeding with frontend logout:', error);
    } finally {
      logout();
      setShowDropdown(false);
      navigate('/');
      console.log('Frontend logout completed');
    }
  };

  const toggleDropdown = (e) => {
    e.stopPropagation();
    setShowDropdown(!showDropdown);
  };

  const handleProfile = (e) => {
    if (e) e.preventDefault();
    setShowDropdown(false);
    if (onNavigate) { onNavigate('/profile'); } else { navigate('/profile'); }
  };

  const handleDashboard = (e) => {
    if (e) e.preventDefault();
    setShowDropdown(false);
    if (onNavigate) {
      onNavigate('/dashboard');
    } else {
      navigate('/dashboard');
    }
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    const names = name.split(' ');
    if (names.length >= 2) {
      return names[0][0] + names[names.length - 1][0];
    }
    return name[0];
  };

  const handleSubscribe = async (planType) => {
    if (!user) {
      console.log('User not logged in, showing auth modal');
      setShowPricingModal(false);
      setTimeout(() => {
        setShowAuthModal(true);
      }, 300);
      return;
    }

    console.log('='.repeat(80));
    console.log('🛒 FRONTEND: Starting subscription process');
    console.log('='.repeat(80));
    console.log('Plan Type:', planType);
    console.log('User:', user.email);
    console.log('='.repeat(80));

    setIsProcessing(true);

    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        console.error('No token found');
        setShowPricingModal(false);
        setShowAuthModal(true);
        return;
      }

      const requestBody = {
        planType: planType,
        email: user.email,
        autoPayment: true
      };

      console.log('📤 REQUEST BODY:', JSON.stringify(requestBody, null, 2));

      const response = await fetch(`${API_BASE_URL}/api/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();
      console.log('📥 RESPONSE:', JSON.stringify(data, null, 2));

      if (response.ok && data.success) {
        console.log('✅ Redirecting to Stripe checkout...');
        console.log('Checkout URL:', data.checkoutUrl);
        
        if (data.checkoutUrl) {
          window.location.href = data.checkoutUrl;
        } else {
          console.error('❌ No checkout URL received');
          alert('Payment initialization failed. Please try again.');
          setIsProcessing(false);
        }
      } else {
        console.error('❌ Checkout session creation failed:', data);
        alert(data.error || 'Failed to create checkout session. Please try again.');
        setIsProcessing(false);
      }
    } catch (error) {
      console.error('❌ Subscription error:', error);
      alert('An error occurred. Please try again.');
      setIsProcessing(false);
    }
  };

  return (
    <>
      <header className="glass" ref={headerRef}>
        <nav>
          <Link to="/" className="logo">
            <img
              className="logo-icon"
              src={`${process.env.PUBLIC_URL || ''}/android-chrome-192x192.png`}
              alt="Terview"
              width="40"
              height="40"
            />
            TERVIEW
          </Link>
                   
          <ul className='nav-links'>
            <li><a href='#showcase' onClick={(e) => handleNavClick(e, '#showcase')}>Products</a></li>
            <li><a href='#features' onClick={(e) => handleNavClick(e, '#features')}>Features</a></li>
            <li><Link to='/desktop'>Desktop App</Link></li>
            <li><Link to='/blog'>Blog</Link></li>
            <li><a href='#pricing' onClick={handlePricingClick}>Pricing</a></li>
          </ul>
          
          <div className="auth-buttons">
            {user ? (
              <div className="user-profile-container" ref={dropdownRef}>
                <div className={`credits-display ${creditsInfo.showActionRequired ? 'action-required' : ''} ${creditsInfo.showUnlimited ? 'unlimited' : ''}`}>
                  {creditsInfo.showUnlimited ? (
                    <span className={`unlimited-badge unlimited-badge--${planTierLabel.toLowerCase()}`}>
                      <svg className="unlimited-badge-icon" width="13" height="13" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                        {/* Filled crown — universally signals premium. */}
                        <path d="M3 7l4.5 3.5L12 4l4.5 6.5L21 7l-1.6 11.2a1 1 0 0 1-1 .8H5.6a1 1 0 0 1-1-.8L3 7z" />
                      </svg>
                      {planTierLabel}
                    </span>
                  ) : creditsInfo.showActionRequired ? (
                    <>
                      <span className="credits-icon" aria-hidden="true">⚠</span>
                      <span className="credits-count action-required-badge">Action required</span>
                    </>
                  ) : (
                    <>
                      <span className="credits-icon" aria-hidden="true">◆</span>
                      <span className="credits-count">{creditsInfo.display}</span>
                    </>
                  )}
                </div>
                
                <div className="user-avatar" onClick={toggleDropdown}>
                  {avatarUrl
                    ? <img src={`${API_BASE_URL}${avatarUrl}`} alt="avatar" className="header-avatar-img" />
                    : <span className="avatar-initials">{getInitials(user.name)}</span>
                  }
                </div>
                
                {showDropdown && (
                  <div className="user-dropdown">
                    <div className="dropdown-header">
                      <div className="dropdown-user-info">
                        <div className="dropdown-name">{user.name}</div>
                        <div className="dropdown-email">{user.email}</div>
                      </div>
                    </div>
                    <div className="dropdown-credits-info">
                      {creditsInfo.showUnlimited ? (
                        <>
                          <span className="credits-label">Status:</span>
                          <span className="credits-value unlimited">✨ Unlimited Access</span>
                        </>
                      ) : creditsInfo.showActionRequired ? (
                        <>
                          <span className="credits-label">Status:</span>
                          <span className="credits-value action-required">⚠️ Payment Due</span>
                        </>
                      ) : (
                        <>
                          <span className="credits-label">Available Credits:</span>
                          <span className="credits-value">⚡ {creditsInfo.display}</span>
                        </>
                      )}
                    </div>
                    <div className="dropdown-divider"></div>
                    <button className="dropdown-item" onClick={handleProfile}>
                      <span className="dropdown-icon">👤</span>
                      Profile
                    </button>
                    <button className="dropdown-item" onClick={handleDashboard}>
                      <span className="dropdown-icon">📊</span>
                      Dashboard
                    </button>
                    <button 
                      className="dropdown-item" 
                      onClick={(e) => {
                        e.preventDefault();
                        setShowDropdown(false);
                        setShowBillingModal(true);
                      }}
                    >
                      <span className="dropdown-icon">💳</span>
                      Billing & Subscription
                    </button>
                    <button className="dropdown-item logout" onClick={handleLogout}>
                      <span className="dropdown-icon">🚪</span>
                      Logout
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <button 
                className="login-btn"
                onClick={() => setShowAuthModal(true)}
              >
                Login/Register
              </button>
            )}
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <ElectronControls />
            <div className='mobile-menu-toggle' ref={mobileMenuToggleRef}>
              <div className='hamburger-line'></div>
              <div className='hamburger-line'></div>
              <div className='hamburger-line'></div>
            </div>
          </div>
        </nav>
        
        <div className='mobile-nav' ref={mobileNavRef}>
          <a href='#showcase' onClick={(e) => handleNavClick(e, '#showcase')}>Products</a>
          <a href='#features' onClick={(e) => handleNavClick(e, '#features')}>Features</a>
          <Link to='/blog'>Blog</Link>
          <a href='#pricing' onClick={handlePricingClick}>Pricing</a>
          {user ? (
            <>
              <div className="mobile-credits">
                {creditsInfo.showUnlimited ? (
                  <>⚡ Status: <span className="unlimited-badge">Unlimited Access</span></>
                ) : creditsInfo.showActionRequired ? (
                  <>⚠️ Status: <span className="action-required-badge">Payment Due</span></>
                ) : (
                  <>⚡ Credits: {creditsInfo.display}</>
                )}
              </div>
              <a href="#dashboard" onClick={handleDashboard}>📊 Dashboard</a>
              <a href="#billing" onClick={(e) => {
                e.preventDefault();
                setShowBillingModal(true);
              }}>💳 Billing</a>
              <a href="#logout" onClick={handleLogout}>🚪 Logout</a>
            </>
          ) : (
            <a href="#login" onClick={() => setShowAuthModal(true)}>Login/Register</a>
          )}
        </div>
      </header>

      <BillingModal
        show={showBillingModal}
        onClose={closeBillingModal}
      />

      {showPricingModal && (
        <div className="pricing-modal-overlay" onClick={() => setShowPricingModal(false)}>
          <div className="pricing-modal-compact glass" onClick={(e) => e.stopPropagation()}>
            <button className="pricing-close-btn" onClick={() => setShowPricingModal(false)}>×</button>
            
            {(() => {
              // Tier the user is currently on, derived once for the modal.
              // subscription_plan may come back as just 'pro' or qualified like
              // 'pro_monthly' / 'pro_yearly', so match by prefix.
              const planRaw = (user?.subscription_plan || '').toLowerCase();
              const isActiveSub = user && (user.subscription_status === 'active' || user.subscription_status === 'cancelling');
              const currentTier = isActiveSub
                ? ['lite', 'pro', 'max'].find(t => planRaw.startsWith(t)) || null
                : null;
              const titleCase = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1) : '';
              return (
                <>
                  <h2 className="pricing-title">{currentTier ? 'Manage your plan' : 'Upgrade your plan'}</h2>
                  <p className="pricing-subtitle">
                    {currentTier ? (
                      <>
                        You're on the <strong style={{ color: '#fff' }}>{titleCase(currentTier)}</strong> plan
                        {user?.subscription_status === 'cancelling' && ' (cancellation pending)'}.
                        {' '}Switch tiers any time — your remaining balance is prorated.
                      </>
                    ) : (
                      <>
                        You're on the <strong style={{ color: '#fff' }}>Free</strong> plan.
                        {' '}Upgrade to unlock the full Terview workspace — more daily applies,
                        AI resume tailoring, and unlimited interview tools.
                      </>
                    )}
                  </p>
                </>
              );
            })()}
            {!user && (
              <div style={{
                background: 'rgba(255, 193, 7, 0.1)',
                border: '1px solid rgba(255, 193, 7, 0.3)',
                borderRadius: '8px',
                padding: '12px',
                marginBottom: '20px',
                textAlign: 'center',
                color: '#ffc107'
              }}>
                ℹ️ Please login or register to subscribe
              </div>
            )}

            {loadingPricing ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <p>Loading current prices...</p>
              </div>
            ) : pricing && pricing.lite ? (
              <>
                {/* Cadence toggle — Monthly / 6 months / Yearly */}
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 24 }}>
                  <div style={{ display: 'inline-flex', padding: 4, borderRadius: 999, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}>
                    {[
                      { key: 'monthly',  label: 'Monthly' },
                      { key: '6months',  label: '6 Months' },
                      { key: 'yearly',   label: 'Yearly' },
                    ].map(({ key, label }) => {
                      const sav = pricing.pro?.[key]?.savings || 0;
                      return (
                        <button key={key}
                          onClick={() => setPricingCadence(key)}
                          style={{
                            padding: '8px 18px', borderRadius: 999, fontSize: 13, fontWeight: 600,
                            background: pricingCadence === key ? '#7c3aed' : 'transparent',
                            color:      pricingCadence === key ? '#fff' : 'rgba(255,255,255,0.8)',
                            border: 'none', cursor: 'pointer',
                          }}>
                          {label}
                          {sav > 0 && (
                            <span style={{
                              marginLeft: 6, padding: '1px 6px', borderRadius: 6, fontSize: 10, fontWeight: 700,
                              background: pricingCadence === key ? 'rgba(255,255,255,0.25)' : 'rgba(74,222,128,0.2)',
                              color: pricingCadence === key ? '#fff' : '#4ade80',
                            }}>
                              −{sav}%
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Three tier cards */}
                <div className="pricing-cards">
                  {[
                    {
                      tier: 'lite',
                      title: 'Lite',
                      tag: null,
                      btnClass: 'subscribe-btn',
                      features: [
                        '✓ Unlimited Live Auto-Apply (LinkedIn, Dice, Indeed, Monster)',
                        '✓ 5 / day on 24/7 Auto-Apply (teaser)',
                        '✗ AI Resume Tailoring',
                        '✓ Unlimited Mock Interviews',
                        '✓ Cover Letter Generator',
                        '✓ AI Interview Questions',
                        '✓ Email support',
                      ],
                    },
                    {
                      tier: 'pro',
                      title: 'Pro',
                      tag: 'Most Popular',
                      btnClass: 'subscribe-btn popular-btn',
                      cardClass: 'popular',
                      features: [
                        '✓ Unlimited Live Auto-Apply',
                        '✓ 50 / day on 24/7 Auto-Apply (Greenhouse, Lever, Workable, Ashby)',
                        '✓ 50 / day AI Resume Tailoring',
                        '✓ Unlimited Mock Interviews',
                        '✓ Cover Letter Generator',
                        '✓ AI Interview Questions',
                        '✓ Priority email support',
                      ],
                    },
                    {
                      tier: 'max',
                      title: 'Max',
                      tag: 'Best Value',
                      btnClass: 'subscribe-btn best-value-btn',
                      cardClass: 'best-value',
                      features: [
                        '✓ Unlimited Live Auto-Apply',
                        '✓ 100 / day on 24/7 Auto-Apply (Greenhouse, Lever, Workable, Ashby)',
                        '✓ 100 / day AI Resume Tailoring',
                        '✓ Unlimited Mock Interviews',
                        '✓ Cover Letter Generator',
                        '✓ AI Interview Questions',
                        '✓ Priority + chat support',
                      ],
                    },
                  ].map(({ tier, title, tag, btnClass, cardClass, features }) => {
                    const tierData = pricing[tier]?.[pricingCadence];
                    if (!tierData) return null;
                    const periodLabel =
                      pricingCadence === 'monthly' ? 'Billed monthly'
                      : pricingCadence === '6months' ? `Billed every 6 months ($${tierData.usd?.toFixed(2)})`
                      : `Billed yearly ($${tierData.usd?.toFixed(2)})`;

                    // Tier-aware button state — same derivation as the title above.
                    const TIER_ORDER = { lite: 1, pro: 2, max: 3 };
                    const planRaw = (user?.subscription_plan || '').toLowerCase();
                    const isActiveSub = user && (user.subscription_status === 'active' || user.subscription_status === 'cancelling');
                    const currentTier = isActiveSub
                      ? ['lite', 'pro', 'max'].find(t => planRaw.startsWith(t)) || null
                      : null;
                    const isCurrent = currentTier === tier;
                    const isUpgrade = currentTier && TIER_ORDER[tier] > TIER_ORDER[currentTier];
                    const isDowngrade = currentTier && TIER_ORDER[tier] < TIER_ORDER[currentTier];

                    let btnLabel;
                    if (isProcessing) btnLabel = 'Processing...';
                    else if (!user) btnLabel = 'Login to Subscribe';
                    else if (isCurrent) btnLabel = '✓ Current Plan';
                    else if (isUpgrade) btnLabel = `Upgrade to ${title}`;
                    else if (isDowngrade) btnLabel = `Downgrade to ${title}`;
                    else btnLabel = `Subscribe to ${title}`;

                    return (
                      <div key={tier} className={`price-card ${cardClass || ''} ${isCurrent ? 'is-current' : ''}`}>
                        {isCurrent ? (
                          <div className="popular-tag" style={{ background: 'linear-gradient(135deg, #00ffff, #e0a3ff)', color: '#0c0418' }}>
                            Your Plan
                          </div>
                        ) : tag && <div className={`popular-tag ${tier === 'max' ? 'best-value-tag' : ''}`}>{tag}</div>}
                        <h3>{title}</h3>
                        <div className="price">
                          <span className="amount">${tierData.monthlyUSD?.toFixed(2)}</span>
                          <span className="period">/month</span>
                        </div>
                        <p className="billing">{periodLabel}</p>
                        {tierData.savings > 0 && <div className="discount">{tierData.savings}% OFF</div>}
                        <ul className="features features-pricing">
                          {features.map((f, i) => (
                            <li key={i} style={f.startsWith('✗') ? { opacity: 0.4, textDecoration: 'line-through' } : null}>
                              {f}
                            </li>
                          ))}
                        </ul>
                        <button
                          className={btnClass}
                          onClick={() => !isCurrent && handleSubscribe(tierData.planType)}
                          disabled={isProcessing || isCurrent}
                          style={isCurrent
                            ? { background: 'rgba(0,255,255,0.10)', color: '#00ffff', border: '1px solid rgba(0,255,255,0.45)', cursor: 'default' }
                            : isDowngrade
                              ? { background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.85)', border: '1px solid rgba(255,255,255,0.18)' }
                              : null
                          }
                        >
                          {btnLabel}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <p>Failed to load pricing. Please try again.</p>
                <button onClick={fetchPricing}>Retry</button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default Header;